import React, { useContext, useEffect, useState } from 'react'
import { darkTheme, defaultTheme } from '@ant-design/compatible'
import { useT } from '@transifex/react'
// PRO-130: deep antd/es/* subpaths + seam LEAF files directly — NOT `src/antd`
// (whose `export * from 'antd'` pulls antd/es/index.js, the whole component
// barrel, into the boot entry chunk). `antd/es/app` and the non-EN locale
// packs below are the carve-out: `update-design` serves neither, so they stay
// on the package until that merge.
import App from 'antd/es/app'
import ConfigProvider from 'antd/es/config-provider'
import { lowerCase } from 'lodash'
import { __RouterContext } from 'react-router'

import { CoreIoUserDatetimeFormatLocaleChoices } from 'src/gen-types'
import { Empty } from 'src/antd/Empty'
import type { Locale } from 'src/antd/locale'
import enGB from 'src/antd/locale/packs/en_GB'
import enUS from 'src/antd/locale/packs/en_US'
// PRO-130: both are leaves (a hook over a plain store, and a string constant).
import { useDesignMode } from 'src/design-mode/useDesignMode'
// PRO-130: leaf too — `antdTheme` reads only `styles/raw` → `styles/palette`,
// which `src/App/index.tsx` already puts on the entry chunk via `ThemeVars`.
import { antdDarkComponents, antdPaletteTokens } from 'src/styles/antdTheme'
import { GEIST_FONT_FAMILY } from 'src/styles/font'
import { AntdStaticsBridge } from 'src/ui/toast/AntdStaticsBridge'

// PRO-130: type-only + leaf imports on purpose. `../api/consts` is a barrel of
// ~90 modules, many of which render antd Select/Tag/Table cells (rc-select/
// rc-table); `../hooks` is a 23-hook barrel. Importing either welds that
// machinery onto the boot entry chunk. AntdI18n is boot-static, so it must
// reach only the leaves it needs.
import type { TCoreIoUserLanguageChoices } from '../api/consts/CoreIoUserLanguageChoices'
import { useMe } from '../hooks/useMe'
// PRO-130: leaf theme hook (gql-free, only @apollo/client makeVar underneath),
// safe to read from this boot-static module. `lightSurfaces` is a leaf too —
// two string constants and no imports at all.
import { isLightSurfacePath } from '../theme/lightSurfaces'
import type { ResolvedTheme } from '../theme/mode'
import { useResolvedTheme } from '../theme/useThemeMode'

// PRO-138: only en_GB + en_US (the EN default set — and en_US is the hard
// fallback used below) ship in the entry chunk. The four non-EN packs, each of
// which drags a full calendar/date-picker locale tree, load on demand from
// `antd/es/locale/*` (was `antd/lib/*`: 48 CJS modules / ~31 KB rendered in an
// otherwise -es bundle, plus all six packs shipped for every user). The active
// locale is known from `me` before first paint, so EN users never flash; a
// non-EN user briefly sees en_GB text until their pack resolves.
const INLINE_LOCALES: Record<string, Locale> = {
  en_GB: enGB,
  en_US: enUS,
}

const LAZY_LOCALES: Record<string, () => Promise<{ default: Locale }>> = {
  ar_EG: () => import('antd/es/locale/ar_EG'),
  da_DK: () => import('antd/es/locale/da_DK'),
  fi_FI: () => import('antd/es/locale/fi_FI'),
  nb_NO: () => import('antd/es/locale/nb_NO'),
  sv_SE: () => import('antd/es/locale/sv_SE'),
}

// Resolve an antd locale pack by code. Inlined packs are returned synchronously;
// lazy packs load via dynamic import, with en_GB standing in until they resolve.
const useAntdLocalePack = (code: string): Locale => {
  const [pack, setPack] = useState<Locale>(() => INLINE_LOCALES[code] ?? enGB)

  useEffect(() => {
    const inline = INLINE_LOCALES[code]
    if (inline) {
      setPack(inline)
      return undefined
    }

    let active = true
    const loader = LAZY_LOCALES[code]
    if (loader) {
      loader().then((module) => {
        if (active) setPack(module.default)
      })
    } else {
      setPack(enGB)
    }

    return () => {
      active = false
    }
  }, [code])

  return pack
}

const DATETIME_TO_LOCALE_CODE: Record<string, string> = {
  DA: 'da_DK',
  EN: 'en_US',
  EN_GB: 'en_GB',
  FI: 'fi_FI',
  SV: 'sv_SE',
  NB: 'nb_NO',
  AR: 'ar_EG',
}

const renderEmpty = () => <Empty />

const AntdI18n: React.FC = ({ children }) => {
  const me = useMe()
  const t = useT()
  const resolvedTheme = useResolvedTheme()
  // Read through the router CONTEXT rather than `useLocation()`, which throws
  // outside a `<Router>`. In the app this component is always inside one
  // (`src/App/index.tsx`); several suites mount it on its own to inspect the
  // antd theme it builds, and a theme provider is not the place to make that
  // impossible. Falls back to the document's own path, which is what a
  // routerless render is showing anyway.
  const routerContext = useContext(__RouterContext)
  const pathname = routerContext?.location.pathname ?? window.location.pathname
  const optionalString = `(${lowerCase(t('optional'))})`
  // The design toggle's font half for antd's own components. antd v5 writes
  // `font-family: <token.fontFamily>` into nearly every component's generated
  // CSS (`resetComponent`, antd/es/style/index.js) and cssinjs injects it
  // UNLAYERED, so the `html body` rule in `src/styles/AppFont.tsx` cannot
  // reach a Button, a Table cell or a Form label — only this token can. See
  // `src/styles/font.ts`.
  //
  // `undefined` in the antd branch, not the antd default spelled out again:
  // spreading `undefined` into an object literal adds nothing.
  const designMode = useDesignMode()
  const fontToken =
    designMode === 'shadcn' ? { fontFamily: GEIST_FONT_FAMILY } : undefined
  // The login page is a fixed light surface (see `src/theme/lightSurfaces.ts`):
  // it carries the wordmark on zinc-100, and antd's algorithm is chosen HERE,
  // once, for the whole session — a nested ConfigProvider cannot scope it,
  // because `hashed: false` means cssinjs writes unhashed `.ant-*` rules that
  // the later-injected token set simply wins app-wide. So the pin happens at
  // the one place that can hold it.
  const isDark = resolvedTheme === 'dark' && !isLightSurfacePath(pathname)
  // Everything antd derives has to follow the same pin, or the algorithm would
  // be light while the seeded palette tokens stayed dark.
  const antdTheme: ResolvedTheme = isDark ? 'dark' : 'light'

  const meLanguage: TCoreIoUserLanguageChoices = me?.language
  const meDateTimeFormat: CoreIoUserDatetimeFormatLocaleChoices =
    me?.datetimeFormatLocale

  // Main ConfigProvider locale (mirrors the previous first-truthy selection):
  // DA -> da_DK (+ overrides below), FI -> fi_FI, NO -> nb_NO, EN+EN ->
  // en_US, EN+EN_GB -> en_GB, anything else -> en_US (the previous
  // `if (!locale) locale = enUS`).
  let mainCode = 'en_US'
  if (meLanguage === 'DA') {
    mainCode = 'da_DK'
  } else if (meLanguage === 'NO') {
    // PRO-975 (HRA, req 1.41 — Norwegian in every interface): Norwegian had
    // no branch, so every antd built-in — Pagination "10 / page", the table
    // sort tooltips, "No data", the requiredMark "(optional)" suffix — fell
    // back to en_US for the Norwegian tenant. The nb_NO pack (already a lazy
    // entry below, used for the NB date format) supplies Pagination, Table,
    // Empty, Modal and the validation messages; Form.optional is patched in
    // below (the pack's Form node has no `optional`).
    mainCode = 'nb_NO'
  } else if (meLanguage === 'FI') {
    // PRO-470/473/476: Finnish had no antd pack, so every antd built-in
    // (Pagination "10 / page", RangePicker "Start date"/"End date", the
    // requiredMark "(optional)" suffix) rendered in the en_US fallback on the
    // fi tenant. fi_FI supplies the Finnish Pagination + DatePicker strings;
    // Form.optional is patched in below (the fi_FI pack ships no Form node).
    mainCode = 'fi_FI'
  } else if (meLanguage === 'EN' && meDateTimeFormat === 'EN_GB') {
    mainCode = 'en_GB'
  }

  const dayjsCode = DATETIME_TO_LOCALE_CODE[meDateTimeFormat] ?? 'en_GB'

  const baseLocale = useAntdLocalePack(mainCode)
  const dayjsLocale = useAntdLocalePack(dayjsCode)

  let locale: Locale = baseLocale
  if (meLanguage === 'DA') {
    locale = {
      ...baseLocale,
      Pagination: {
        // Options.jsx
        items_per_page: t('/ page'),
        jump_to: t('Jump to'),
        jump_to_confirm: t('confirm'),
        page: t('Page'),

        // Pagination.jsx
        prev_page: t('Previous page'),
        next_page: t('Next page'),
        prev_5: t('Previous {count} pages', { count: 5 }),
        next_5: t('Next {count} pages', { count: 5 }),
        prev_3: t('Previous {count} pages', { count: 3 }),
        next_3: t('Next {count} pages', { count: 3 }),
      },
      Form: {
        ...baseLocale.Form,
        defaultValidateMessages: {
          ...baseLocale.Form?.defaultValidateMessages,
        },
        optional: optionalString,
      },
    }
  } else if (meLanguage === 'NO') {
    // PRO-975: the nb_NO pack's Form node carries the validation messages but
    // no `optional`, so the suffix would fall back to antd's English. Patched
    // with the bokmål word itself rather than the Transifex `optional` key:
    // this completes a pack that is static bokmål text throughout, and it
    // cannot fall back to English while the CDS is unreachable.
    locale = {
      ...baseLocale,
      Form: {
        ...baseLocale.Form,
        defaultValidateMessages: {
          ...baseLocale.Form?.defaultValidateMessages,
        },
        optional: '(valgfritt)',
      },
    }
  } else if (meLanguage === 'FI') {
    // PRO-476: the fi_FI pack has Finnish Pagination/DatePicker but NO Form
    // node, so the "(optional)" suffix would fall back to antd's English
    // default. Patch just Form.optional (from the Transifex `optional` key);
    // Pagination/DatePicker come from the fi_FI pack unchanged.
    locale = {
      ...baseLocale,
      Form: {
        ...baseLocale.Form,
        defaultValidateMessages: {
          ...baseLocale.Form?.defaultValidateMessages,
        },
        optional: optionalString,
      },
    }
  }

  return (
    <ConfigProvider
      form={{ requiredMark: 'optional' }}
      locale={{
        ...locale,
        DatePicker: dayjsLocale.DatePicker,
        Calendar: dayjsLocale.Calendar,
        TimePicker: dayjsLocale.TimePicker,
      }}
      renderEmpty={renderEmpty}
      // PRODUCT-154: the two @ant-design/compatible v4 themes supply the
      // ALGORITHM (light vs dark derivative) plus the v4 shape tokens
      // (borderRadius 2, wireframe). hashed:false is required so the unhashed
      // .ant-* classes stay stable across the theme swap.
      //
      // Colour-token programme batch 2: every colour antd derives is now seeded
      // from `src/styles/palette.ts` via `src/styles/antdTheme.ts` — read that
      // module's header before changing anything here, in particular for which
      // keys are SEEDS (re-derived per theme) and which land literally. This
      // deliberately repaints antd surfaces in BOTH themes; it also replaces the
      // hand-written dark hexes that used to sit inline here, which were a
      // second source of truth able to drift from the palette silently
      // (PRO-509's dark elevation ladder is unchanged — it now comes from the
      // palette tokens that define it).
      theme={{
        ...(isDark ? darkTheme : defaultTheme),
        hashed: false,
        token: {
          ...(isDark ? darkTheme.token : defaultTheme.token),
          ...antdPaletteTokens(antdTheme),
          ...fontToken,
        },
        ...(isDark ? { components: antdDarkComponents() } : {}),
      }}
    >
      <App>
        <AntdStaticsBridge />
        {children}
      </App>
    </ConfigProvider>
  )
}

export default AntdI18n
